import * as React from "react"
import {Slot} from "@radix-ui/react-slot"
import type {VariantProps} from "class-variance-authority"
import {X} from "lucide-react"

import {cn} from "@/shared/utils/utils.ts"
import {badgeVariants} from "./Badge.variants"

interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
  VariantProps<typeof badgeVariants> {
  asChild?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  removable?: boolean
  onRemove?: () => void
  dot?: boolean
}

const Badge = React.memo(function Badge({ className, variant, size, interactive, asChild, leftIcon, rightIcon, removable, onRemove, dot, children, ...props }: BadgeProps): React.ReactElement {
  const Comp = asChild ? Slot : "div"
  return (
    <Comp className={cn(badgeVariants({ variant, size, interactive }), className)} {...props}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-60" aria-hidden="true" />}
      {leftIcon && <span className="-ml-0.5 inline-flex items-center">{leftIcon}</span>}
      <span className="truncate leading-none">{children}</span>
      {rightIcon && <span className="-mr-0.5 inline-flex items-center">{rightIcon}</span>}
      {removable && (
        <button
          type="button"
          aria-label="Remove badge"
          onClick={onRemove}
          className="ml-1 -mr-1 rounded-full p-1.5 sm:p-1 hover:bg-background/30 active:bg-background/30 focus-visible:ring-2 focus-visible:ring-ring min-h-touch min-w-touch sm:min-h-0 sm:min-w-0 touch-manipulation min-h-11 min-w-11"
        >
          <X className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
        </button>
      )}
    </Comp>
  )
});

export { Badge }
